import axios, { AxiosError } from 'axios'
import type { IUserStoreState } from '../types/IUserStoreState'
import type { IUserType } from '../types/IUserType'
import type { IConsentAgreement } from '../types/IConsentAgreement'
import type { IPatchUserProfilePayload } from '../types/IPatchUserProfilePayload'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import { logger } from '@/utils/eventLogger'

export const useUserStore = defineStore('userStore', {
  state: (): IUserStoreState => ({
    // Инициализируем user как null, пока не получим реальные данные
    user: null,
    consentAgreement: null,
  }),

  actions: {
    // Инициализирует данные пользователя в сторе
    initializeUser(payload: { user: IUserType }) {
      // Деструктурируем объект user из payload
      const { user } = payload
      this.user = user
    },

    /**
     * Загружает профиль текущего пользователя из IAM и актуализирует права доступа.
     */
    async getUserProfile(options?: { authRecovery?: boolean; isCurrent?: () => boolean }) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const requestConfig = { headers: {}, skipAuthLogout: options?.authRecovery === true }
        const permissionsStore = usePermissionsStore()
        const speculativePermissions = permissionsStore
          .fetchPermissions({ speculative: true })
          .then(() => true, () => false)
        const response = await axios.get(`${BASE_URL}/api/account/iam/`, requestConfig)
        if (options?.isCurrent && !options.isCurrent()) return

        if (response.status === 200) {
          this.initializeUser({ user: response.data })

          if (!await speculativePermissions) {
            try {
              await permissionsStore.fetchPermissions()
            } catch (e) {
              logger.warn('[UserStore] Ошибка загрузки прав пользователя:', e)
            }
          }

          return response
        }
      } catch (error) {
        if (options?.authRecovery) throw error
        if (error instanceof AxiosError) {
          const message = error?.response?.data?.errors?.[0]?.detail || 'Неизвестная ошибка'
          feedbackStore.showToast({
            type: 'error',
            message,
            time: 7000,
          })
        }
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async deleteUserAvatar() {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.delete(`${BASE_URL}/api/account/avatar/`)

        if (response.status === 200 || response.status === 204) {
          feedbackStore.showToast({
            type: 'success',
            message: 'Аватар успешно удален',
            time: 7000,
          })
          await this.getUserProfile()
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const message = error?.response?.data?.errors?.[0]?.detail || 'Неизвестная ошибка'
          feedbackStore.showToast({
            type: 'error',
            message,
            time: 7000,
          })
        }
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async setUserAvatar(file: File) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await axios.put(`${BASE_URL}/api/account/avatar/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        if (response.status === 200) {
          feedbackStore.showToast({
            type: 'success',
            message: 'Аватар успешно обновлён',
            time: 7000,
          })
          await this.getUserProfile()
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const message = error?.response?.data?.errors?.[0]?.detail || 'Неизвестная ошибка'
          feedbackStore.showToast({
            type: 'error',
            message,
            time: 7000,
          })
        }
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async getConsentText(): Promise<string> {
      const feedbackStore = useFeedbackStore()
      try {
        const response = await axios.get(`${BASE_URL}/api/account/personal-data/consent/text/`)
        return response.data?.text
      } catch (error) {
        logger.error('[UserStore] Ошибка при получении шаблона согласия ПД:', error)
        const message = getApiError(error, 'Ошибка при получении шаблона согласия ПД')
        feedbackStore.showToast({
          type: 'error',
          message,
          time: 7000,
        })
        return ''
      }
    },

    async getConsentAgreement(): Promise<IConsentAgreement | null> {
      try {
        const response = await axios.get(`${BASE_URL}/api/account/personal-data/consent/agree/`)
        if (response.status === 200) {
          this.consentAgreement = response.data?.result
          logger.info('[UserStore] Получен ответ согласия ПД:', JSON.stringify(response.data))
        }
      } catch (error) {
        logger.error('[UserStore] Ошибка при получении согласия ПД:', error)
      }
      return null
    },

    async patchUserProfile(payload: IPatchUserProfilePayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch(`${BASE_URL}/api/account/iam/`, payload)
        if (response.status === 200) {
          feedbackStore.showToast({
            type: 'success',
            message: 'Профиль успешно сохранен',
            time: 5000,
          })
          await this.getUserProfile()
          return true
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const message = error?.response?.data?.errors?.[0]?.detail || 'Не удалось обновить профиль'
          feedbackStore.showToast({
            type: 'error',
            message,
            time: 7000,
          })
        }
      } finally {
        feedbackStore.isGlobalLoading = false
      }
      return false
    },

    async agreeToConsent() {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/account/personal-data/consent/agree/`)

        if (response.status === 200 || response.status === 201) {
          feedbackStore.showToast({
            type: 'success',
            message: 'Согласие успешно подписано',
            time: 7000,
          })
          // Профиль — чтобы закрыть диалог (is_terms_accepted), согласие — чтобы в профиле сразу был новый PDF
          await this.getUserProfile()
          await this.getConsentAgreement()
          return true
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const message = error?.response?.data?.errors?.[0]?.detail || 'Ошибка при подписании согласия'
          feedbackStore.showToast({
            type: 'error',
            message,
            time: 7000,
          })
        }
      } finally {
        feedbackStore.isGlobalLoading = false
      }
      return false
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
